import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@astro/api'
import { CourseCard, CourseCardSkeleton, decorateCourse } from '@astro/course-card'
import { categoryPill } from './coursesData'
import { useRevealClass } from '../../../hooks/useRevealClass'
import { useToast } from '../../../context/ToastContext'
import './CoursesCatalogSection.css'
import { appUrl } from '@astro/config'

export default function CoursesCatalogSection() {
  const { showToast } = useToast()
  const [filter, setFilter] = useState('all')

  const headerReveal = useRevealClass()
  const statsReveal = useRevealClass()
  const trustStripReveal = useRevealClass()

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['courses'],
    queryFn: () => api.courses.list(),
  })

  // GET /courses is a flat API-resource collection: `data` is the course
  // array itself, with pagination under `meta` (apiClient already unwraps
  // the axios response) — same read as the education app's catalog.
  const courses = useMemo(
    () => (data?.data ?? []).map((c, i) => decorateCourse(c, i)),
    [data],
  )
  const totalCourses = data?.meta?.total ?? courses.length

  // Filter tabs come from the categories actually present in the payload.
  const filterTabs = useMemo(() => {
    const seen = new Map()
    for (const c of courses) if (!seen.has(c.category)) seen.set(c.category, categoryPill(c.category))
    return [
      { key: 'all', label: 'All Courses' },
      ...[...seen.entries()].map(([key, label]) => ({ key, label })),
    ]
  }, [courses])

  const filtered = useMemo(
    () => courses.filter((c) => filter === 'all' || c.category === filter),
    [courses, filter],
  )

  // Show only the first 4 courses in this section.
  const visible = useMemo(() => filtered.slice(0, 4), [filtered])

  return (
    <section id="courses-catalog" className="relative overflow-hidden" style={{ background: '#FAF7F2' }}>
      <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
        <div className="absolute -top-40 -right-40" style={{ width: '560px', height: '560px', background: 'radial-gradient(circle,rgba(200,146,42,.07) 0%,transparent 65%)', filter: 'blur(70px)', borderRadius: '50%' }} />
        <div className="absolute -bottom-40 -left-40" style={{ width: '480px', height: '480px', background: 'radial-gradient(circle,rgba(200,146,42,.05) 0%,transparent 65%)', filter: 'blur(60px)', borderRadius: '50%' }} />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[min(900px,90vw)] h-px" style={{ background: 'linear-gradient(90deg,transparent,rgba(200,146,42,.2),transparent)' }} />
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[min(900px,90vw)] h-px" style={{ background: 'linear-gradient(90deg,transparent,rgba(200,146,42,.12),transparent)' }} />
        <div className="absolute inset-0 opacity-[.018]" style={{ backgroundImage: 'radial-gradient(circle,#C8922A 1px,transparent 1px)', backgroundSize: '32px 32px' }} />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="flex flex-col lg:flex-row items-start lg:items-end justify-between gap-8 pt-24 pb-14" style={{ borderBottom: '1px solid rgba(200,146,42,.12)' }}>
          <div ref={headerReveal.ref} className={`cc-card-wrap${headerReveal.visible ? ' cc-in-view' : ''}`} style={{ transitionDelay: '.02s' }}>
            <div className="flex items-center gap-2.5 mb-5">
              <span className="font-royal text-base leading-none" style={{ color: '#C8922A' }}>ॐ</span>
              <div className="flex items-center gap-2">
                <div className="w-4 h-px" style={{ background: 'rgba(200,146,42,.4)' }} />
                <span className="text-[9px] uppercase font-bold tracking-[.45em] font-royal" style={{ color: 'rgba(200,146,42,.65)' }}>
                  ◆ Learn from Maharaj Ji ◆
                </span>
                <div className="w-4 h-px" style={{ background: 'rgba(200,146,42,.4)' }} />
              </div>
            </div>
            <h2 className="font-royal font-normal leading-none tracking-tight">
              <span className="block text-4xl sm:text-5xl lg:text-[52px]" style={{ color: '#1C0A03' }}>
                Certified
              </span>
              <span
                className="block text-3xl sm:text-5xl lg:text-[52px]"
                style={{ background: 'linear-gradient(90deg,#C8922A 0%,#E5A820 50%,#C8922A 100%)', WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent' }}
              >
                Astrology Courses
              </span>
            </h2>
            <p className="text-[14px] mt-4 max-w-sm leading-[1.85]" style={{ color: '#8B7355', fontWeight: 300 }}>
              Live online and offline classes in Astrology, Palmistry &amp; Vastu — made simple for beginners and experts.
            </p>
          </div>

          <div ref={statsReveal.ref} className={`cc-card-wrap flex flex-col items-start lg:items-end gap-5 flex-shrink-0${statsReveal.visible ? ' cc-in-view' : ''}`} style={{ transitionDelay: '.08s' }}>
            <div className="flex items-center gap-5">
              <div className="text-center">
                <p className="font-royal text-[22px] font-bold leading-none" style={{ color: '#1C0A03' }}>{totalCourses || '—'}</p>
                <p className="text-[8px] uppercase tracking-widest mt-1" style={{ color: '#9C8470' }}>Courses</p>
              </div>
              <div className="w-px h-8" style={{ background: 'rgba(200,146,42,.2)' }} />
              <div className="text-center">
                <p className="font-royal text-[22px] font-bold leading-none" style={{ color: '#1C0A03' }}>500+</p>
                <p className="text-[8px] uppercase tracking-widest mt-1" style={{ color: '#9C8470' }}>Students</p>
              </div>
              <div className="w-px h-8" style={{ background: 'rgba(200,146,42,.2)' }} />
              <div className="text-center">
                <p className="font-royal text-[22px] font-bold leading-none" style={{ color: '#1C0A03' }}>4.9★</p>
                <p className="text-[8px] uppercase tracking-widest mt-1" style={{ color: '#9C8470' }}>Avg Rating</p>
              </div>
            </div>
            <a
              href={appUrl("education", "classes")}
              className="inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full text-[10px] uppercase tracking-[.26em] font-bold transition-all duration-300"
              style={{ background: '#4A1A0A', color: '#FDE68A', boxShadow: '0 4px 20px rgba(74,26,10,.3)' }}
              onMouseOver={(e) => { e.currentTarget.style.background = '#6B2510'; e.currentTarget.style.boxShadow = '0 8px 28px rgba(74,26,10,.42)' }}
              onMouseOut={(e) => { e.currentTarget.style.background = '#4A1A0A'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(74,26,10,.3)' }}
              onClick={() => showToast('Opening full course catalog…')}
            >
              Explore All Courses
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
              </svg>
            </a>
          </div>
        </div>

        <div className="flex items-center gap-2 overflow-x-auto no-scrollbar py-7" id="cc-filter-tabs">
          {filterTabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`cc-filter-tab${filter === tab.key ? ' cc-tab-active' : ''}`}
              onClick={() => setFilter(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {isError && (
          <div className="pb-20 text-center">
            <div className="text-5xl mb-4 opacity-40">🛰️</div>
            <p className="font-royal text-lg font-bold mb-1" style={{ color: '#1C0A03' }}>
              Couldn’t Load Courses
            </p>
            <p className="text-sm mb-5" style={{ color: '#8B7355' }}>
              Something went wrong while fetching live data. Please try again.
            </p>
            <button
              type="button"
              onClick={() => refetch()}
              className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest px-6 py-3 rounded-full border transition-colors"
              style={{ borderColor: 'rgba(200,146,42,.35)', color: '#C8922A', background: 'rgba(200,146,42,.06)' }}
            >
              Retry
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 pb-20" id="cc-cards-grid">
          {isPending
            ? Array.from({ length: 4 }, (_, i) => <CourseCardSkeleton key={i} />)
            : visible.map((course) => (
                <CourseCard
                  key={course.id}
                  course={course}
                  href={appUrl('education', `classes/${course.slug ?? course.id}`)}
                />
              ))}
        </div>

        <div
          ref={trustStripReveal.ref}
          className={`cc-card-wrap flex flex-col sm:flex-row items-center justify-between gap-6 pb-20 pt-2${trustStripReveal.visible ? ' cc-in-view' : ''}`}
          style={{ borderTop: '1px solid rgba(200,146,42,.1)', transitionDelay: '.38s' }}
        >
          <div className="flex flex-wrap items-center justify-center sm:justify-start gap-5">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'rgba(200,146,42,.1)', fontSize: '15px' }}>🎓</div>
              <div>
                <p className="text-xs font-bold" style={{ color: '#1C0A03' }}>Industry Recognised</p>
                <p className="text-[8px] uppercase tracking-widest" style={{ color: '#9C8470' }}>Certificates</p>
              </div>
            </div>
            <div className="w-px h-8 hidden sm:block" style={{ background: 'rgba(200,146,42,.15)' }} />
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'rgba(200,146,42,.1)', fontSize: '15px' }}>♾️</div>
              <div>
                <p className="text-xs font-bold" style={{ color: '#1C0A03' }}>Lifetime Access</p>
                <p className="text-[8px] uppercase tracking-widest" style={{ color: '#9C8470' }}>All Devices</p>
              </div>
            </div>
            <div className="w-px h-8 hidden sm:block" style={{ background: 'rgba(200,146,42,.15)' }} />
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'rgba(200,146,42,.1)', fontSize: '15px' }}>💬</div>
              <div>
                <p className="text-xs font-bold" style={{ color: '#1C0A03' }}>Live Support</p>
                <p className="text-[8px] uppercase tracking-widest" style={{ color: '#9C8470' }}>24 × 7</p>
              </div>
            </div>
          </div>

          <a
            href="#"
            className="inline-flex items-center gap-3 text-[10px] uppercase tracking-[.24em] font-bold px-7 py-3.5 rounded-full transition-all duration-300"
            style={{ border: '1.5px solid rgba(200,146,42,.28)', color: '#8B7355' }}
            onMouseOver={(e) => { e.currentTarget.style.borderColor = 'rgba(200,146,42,.55)'; e.currentTarget.style.color = '#C8922A'; e.currentTarget.style.background = 'rgba(200,146,42,.05)' }}
            onMouseOut={(e) => { e.currentTarget.style.borderColor = 'rgba(200,146,42,.28)'; e.currentTarget.style.color = '#8B7355'; e.currentTarget.style.background = 'transparent' }}
          >
            View All {totalCourses || ''} Courses
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
            </svg>
          </a>
        </div>
      </div>
    </section>
  )
}
